import { useNavigate } from 'react-router-dom';

import { EmptyScreen, Spinner } from '@components';
import CustomTable from '@components/custom-table/CustomTable';
import { CustomTableRowData } from '@components/custom-table/types';
import CustomTooltip from '@components/tooltip/Tooltip';
import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import { LEAD_MANAGEMENT } from '@constants/routes';
import ActionButton from '@features/lead-management/components/ActionButton';
import { leadsTableColumn } from '@features/lead-management/constants/leadList';
import {
  formatMobileNumber,
  getTableColumns
} from '@features/lead-management/utils/common';
import useIsTruncated from '@hooks/useIsTruncated';
import { StatusEntity } from 'types/lead-management';

import { LeadTableProps } from './types';

import LeadAssignee from '../../components/LeadAssignee';
import LeadStatusTag from '../../components/LeadStatusTag';
import LeadTags from '../../components/LeadTags';
import { LeadSource } from '../../constants';
import { UNASSIGNED_ASSIGNEE } from '../../constants/common';
import useCanEditLead from '../../hooks/useCanEditLead';
import useLeadSources from '../../hooks/useLeadSources';
import { LeadType } from '../../types/common';

const TruncatedCell = ({
  text,
  tooltipId
}: {
  text: string;
  tooltipId: string;
}) => {
  const { ref, isTruncated } = useIsTruncated<HTMLDivElement>(text);
  return (
    <>
      <div
        ref={ref}
        data-tooltip-id={isTruncated ? tooltipId : undefined}
        className="max-w-50 truncate text-base font-medium"
      >
        {text}
      </div>
      {isTruncated && (
        <CustomTooltip
          id={tooltipId}
          place="top"
          className="!max-w-400 !break-all"
        >
          {text}
        </CustomTooltip>
      )}
    </>
  );
};

const LeadTable = ({
  page,
  setPage,
  selectedRows,
  setSelectedRows,
  onUpdateAssignee,
  leadDetails,
  handleAddTagButtonClick,
  agentsList,
  handleDeleteLead,
  isListLoading,
  isAssigneeDisabled = false,
  canMultiSelect = false,
  hideDeleteAction = false,
  sortState,
  onSortChange
}: LeadTableProps) => {
  const navigate = useNavigate();
  const { getSourceLabel } = useLeadSources();
  const canEditLead = useCanEditLead();
  const leadList = leadDetails?.data ?? [];
  const pagination = leadDetails?.pagination;

  const rowActions = canEditLead
    ? ['edit', ...(hideDeleteAction ? [] : ['delete'])]
    : [];

  const getAssigneeOptions = (rowAssignee?: LeadType['assignee']) => {
    const options = [
      UNASSIGNED_ASSIGNEE,
      ...agentsList.map((agent) => ({
        id: agent.user_id,
        name: agent.name
      }))
    ];

    const assigneeId = rowAssignee?.id || rowAssignee?.user_id;
    const isKnownAssignee = options.some((option) => option.id === assigneeId);

    if (assigneeId && rowAssignee?.name && !isKnownAssignee) {
      options.push({ id: assigneeId, name: rowAssignee.name });
    }

    return options;
  };

  const handleAssigneeSelect = (
    leadId: string,
    assignee: {
      id: string;
      isUnassigned: boolean;
    }
  ) => {
    if (onUpdateAssignee && assignee) {
      onUpdateAssignee(leadId, assignee);
    }
  };

  const handleEditLead = (leadId: string) => {
    if (leadId) {
      navigate(LEAD_MANAGEMENT.LEAD_EDIT.replace(':leadId', leadId));
    }
  };

  const renderTableCell = (value: string) => (
    <div className="w-full truncate text-base font-medium">
      {value || DEFAULT_PLACEHOLDER}
    </div>
  );

  const renderNameCell = (id: string, name: string) => {
    if (!name) {
      return renderTableCell(name);
    }
    return <TruncatedCell text={name} tooltipId={`name-tooltip-${id}`} />;
  };

  const getSourceDisplay = (row: LeadType) => {
    const baseSource = getSourceLabel(row.source) || DEFAULT_PLACEHOLDER;
    const tooltipId = `source-tooltip-${row.id}`;

    if (row.source === LeadSource.FACEBOOK_ADS && row.ad?.name) {
      const displayText = `Facebook Ad: ${row.ad.name}`;
      return <TruncatedCell text={displayText} tooltipId={tooltipId} />;
    }

    return <TruncatedCell text={baseSource} tooltipId={tooltipId} />;
  };

  const modifiedTableData = leadList.map((row: LeadType) => ({
    id: row.id,
    customerName: renderNameCell(row.id, row.name),
    mobileNumber: (
      <TruncatedCell
        text={
          formatMobileNumber(row.mobile_number, row.country_code) ||
          DEFAULT_PLACEHOLDER
        }
        tooltipId={`mobile-tooltip-${row.id}`}
      />
    ),
    tags: (
      <div className="max-w-70">
        <LeadTags
          tags={row.tags}
          handleAddTagButtonClick={() => handleAddTagButtonClick?.(row.id)}
          showAddButton={false}
        />
      </div>
    ),
    source: getSourceDisplay(row),
    assignee: (
      <LeadAssignee
        assignee={{
          id: row.assignee?.id || row.assignee?.user_id,
          name: row.assignee?.name || '',
          isUnassigned: !row.assignee?.name
        }}
        assigneeOptions={getAssigneeOptions(row.assignee)}
        handleAssigneeSelect={(assignee) =>
          handleAssigneeSelect(row.id, assignee)
        }
        disabled={isAssigneeDisabled || !canEditLead}
      />
    ),
    daysStuck: (
      <div className="text-base font-medium">
        {row.days_stuck ?? DEFAULT_PLACEHOLDER}
      </div>
    ),
    status: <LeadStatusTag status={row.status as StatusEntity} />,
    actions: (
      <ActionButton
        item={row}
        handleEditAction={() => handleEditLead(row.id)}
        handleDeleteAction={() => handleDeleteLead(row.id)}
        actions={rowActions}
      />
    )
  }));

  const handleRowClick = (row: CustomTableRowData<string>) => {
    navigate(`${LEAD_MANAGEMENT.LEAD_DETAILS.replace(':leadId', row.id)}`);
  };

  const handleSelect = (row: CustomTableRowData<string>) => {
    if (selectedRows.includes(row.id)) {
      setSelectedRows(selectedRows.filter((id) => id !== row.id));
    } else {
      setSelectedRows([...selectedRows, row.id]);
    }
  };

  const handleSelectAll = (selectedIds: string[], removedIds: string[]) => {
    if (selectedIds.length > 0) {
      setSelectedRows([...selectedRows, ...selectedIds]);
    }
    if (removedIds.length > 0) {
      setSelectedRows(selectedRows.filter((id) => !removedIds.includes(id)));
    }
  };

  if (isListLoading) {
    return (
      <div className="mt-4 flex min-h-[50vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!leadList.length) {
    return (
      <div className="mt-4 min-h-[50vh]">
        <EmptyScreen />
      </div>
    );
  }

  return (
    <div className="mt-4">
      <CustomTable
        headers={getTableColumns(leadsTableColumn, rowActions.length > 0)}
        enableSort
        sortState={sortState}
        onSortChange={onSortChange}
        hasMultiSelect={canMultiSelect && canEditLead}
        selectedRows={selectedRows}
        handleSelect={handleRowClick}
        handleCheckboxSelect={handleSelect}
        handleSelectAll={handleSelectAll}
        data={modifiedTableData ?? []}
        totalCount={pagination?.total_rows ?? 0}
        currentPage={page + 1}
        onPageChange={(count: number) => setPage(count - 1)}
        showGoToPage
        minWidth={1280}
      />
    </div>
  );
};

export default LeadTable;
